import {
  AEO_ORG_LEGAL_NAME,
  AEO_SITE_NAME,
  AEO_SITE_URL,
} from './constants';
import { buildAeoCatalog, type AeoProduct } from './catalog';

export function buildOrganizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${AEO_SITE_URL}/#organization`,
    name: AEO_ORG_LEGAL_NAME,
    alternateName: AEO_SITE_NAME,
    url: AEO_SITE_URL,
    logo: `${AEO_SITE_URL}/assets/images/selflabs-social-share.png`,
    description:
      'SELF Labs is an AI-native innovation lab building SELF Phone, humanoid robotics, Living Websites, Resonatia AI API, and conscious systems technology.',
    sameAs: ['https://milancheeks.com'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: AEO_SITE_URL,
    },
  };
}

export function buildWebSiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${AEO_SITE_URL}/#website`,
    name: AEO_SITE_NAME,
    url: AEO_SITE_URL,
    publisher: { '@id': `${AEO_SITE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${AEO_SITE_URL}/products?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

function productJsonLd(p: AeoProduct): Record<string, unknown> {
  const numericPrice = p.price?.replace(/[^0-9.]/g, '');
  return {
    '@type': 'Product',
    '@id': `${p.url}#product`,
    name: p.name,
    description: p.description,
    url: p.url,
    brand: { '@type': 'Brand', name: AEO_SITE_NAME },
    category: p.category,
    ...(numericPrice
      ? {
          offers: {
            '@type': 'Offer',
            url: p.url,
            priceCurrency: 'USD',
            price: numericPrice,
            availability: 'https://schema.org/InStock',
            seller: { '@id': `${AEO_SITE_URL}/#organization` },
          },
        }
      : {}),
  };
}

export function buildProductListJsonLd(catalog?: AeoProduct[]): Record<string, unknown> {
  const items = catalog ?? buildAeoCatalog();
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${AEO_SITE_NAME} Products`,
    url: `${AEO_SITE_URL}/products`,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 24).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: productJsonLd(p),
    })),
  };
}

export function buildGlobalAeoJsonLd(): Record<string, unknown> {
  const stripContext = ({ '@context': _c, ...rest }: Record<string, unknown>) => rest;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      stripContext(buildOrganizationJsonLd()),
      stripContext(buildWebSiteJsonLd()),
      stripContext(buildProductListJsonLd()),
    ],
  };
}

export function buildRoboticsProductJsonLd(
  product: import('../roboticsProducts').RoboticsProduct
): Record<string, unknown> {
  const image = product.images[0]?.src;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.shortDescription,
    url: `${AEO_SITE_URL}/robotics/${product.id}`,
    image: image?.startsWith('http') ? image : `${AEO_SITE_URL}${image}`,
    brand: { '@type': 'Brand', name: AEO_SITE_NAME },
    category: 'Robotics',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: (product.priceCents / 100).toFixed(2),
      availability: 'https://schema.org/InStock',
      url: `${AEO_SITE_URL}/robotics/${product.id}`,
      seller: { '@type': 'Organization', name: AEO_ORG_LEGAL_NAME },
    },
  };
}

export function buildSelfPhoneProductJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'SELF Phone',
    description:
      'AI-native smartphone and Resonatia mesh node from SELF Labs — download edge models, earn ESSENCE, privacy-first design.',
    url: `${AEO_SITE_URL}/self-phone`,
    brand: { '@type': 'Brand', name: AEO_SITE_NAME },
    category: 'Hardware',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: '999.00',
      availability: 'https://schema.org/LimitedAvailability',
      url: `${AEO_SITE_URL}/self-phone`,
      seller: { '@type': 'Organization', name: AEO_ORG_LEGAL_NAME },
    },
  };
}
